import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, Plug, Plus, Save, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, ApiError } from '@/lib/api';
import { createEntity, deleteEntity, patchEntity } from '@/lib/mutations';

export const Route = createFileRoute('/admin/entities')({
	component: EntitiesAdminPage,
});

interface AdminEntity {
	id: string;
	code: string;
	name: string;
	intake_email: string;
	inkoop_email: string | null;
	verkoop_email: string | null;
	active: number | boolean;
}

function EntitiesAdminPage() {
	const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
	const queryClient = useQueryClient();
	const entitiesQuery = useQuery({
		queryKey: ['admin-entities'],
		queryFn: async () => {
			const r = await fetch('/api/entities', { credentials: 'include' });
			if (!r.ok) throw new ApiError(r.status, await r.json().catch(() => ({})));
			return r.json() as Promise<{ entities: AdminEntity[] }>;
		},
		enabled: meQuery.data?.role === 'admin',
	});

	if (meQuery.isLoading) return <div className="p-8 text-sm text-muted-foreground">Laden...</div>;
	if (meQuery.data?.role !== 'admin') {
		return (
			<div className="mx-auto max-w-md p-8 text-center text-sm">
				<AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" />
				Alleen admins kunnen entiteiten beheren.
			</div>
		);
	}

	const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-entities'] });

	return (
		<div className="mx-auto max-w-4xl space-y-6">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">Entiteiten</h1>
				<p className="text-sm text-muted-foreground">
					Een nieuwe entiteit aanmaken creëert direct een Cloudflare Email Routing-rule. Documenten worden opgeslagen
					met `&lt;entity_id&gt;/` als prefix in één gedeelde R2-bucket.
				</p>
			</div>

			<NewEntityForm onCreated={refresh} />

			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-medium">Bestaande entiteiten</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3 p-4 pt-0">
					{entitiesQuery.isLoading && <p className="text-sm text-muted-foreground">Laden...</p>}
					{entitiesQuery.data?.entities.map((e) => (
						<EntityRow key={e.id} entity={e} onChanged={refresh} />
					))}
				</CardContent>
			</Card>
		</div>
	);
}

function NewEntityForm({ onCreated }: { onCreated: () => void }) {
	const [open, setOpen] = useState(false);
	const [code, setCode] = useState('');
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [inkoopEmail, setInkoopEmail] = useState('');
	const [verkoopEmail, setVerkoopEmail] = useState('');
	const [error, setError] = useState<string | null>(null);

	const mutation = useMutation({
		mutationFn: () => {
			const body: {
				code: string;
				name: string;
				intake_email: string;
				inkoop_email?: string;
				verkoop_email?: string;
			} = {
				code: code.trim().toLowerCase(),
				name: name.trim(),
				intake_email: email.trim().toLowerCase(),
			};
			const inkoop = inkoopEmail.trim().toLowerCase();
			if (inkoop) body.inkoop_email = inkoop;
			const verkoop = verkoopEmail.trim().toLowerCase();
			if (verkoop) body.verkoop_email = verkoop;
			return createEntity(body);
		},
		onSuccess: () => {
			setError(null);
			setCode('');
			setName('');
			setEmail('');
			setInkoopEmail('');
			setVerkoopEmail('');
			setOpen(false);
			onCreated();
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	if (!open) {
		return (
			<Button size="sm" onClick={() => setOpen(true)}>
				<Plus className="mr-1.5 h-4 w-4" />
				Nieuwe entiteit
			</Button>
		);
	}

	// Email auto-suggest op basis van code
	const suggestEmail = code ? `${code}@in.docr.nl` : '';
	const suggestInkoop = code ? `inkoop.${code}@in.docr.nl` : '';
	const suggestVerkoop = code ? `verkoop.${code}@in.docr.nl` : '';

	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between pb-3">
				<CardTitle className="text-sm font-medium">Nieuwe entiteit</CardTitle>
				<Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
					<X className="h-4 w-4" />
				</Button>
			</CardHeader>
			<CardContent className="space-y-3 p-4 pt-0">
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<div>
						<Label>Code</Label>
						<Input
							value={code}
							onChange={(e) => setCode(e.target.value.toLowerCase())}
							placeholder="bv. suregroup"
						/>
						<p className="mt-1 text-xs text-muted-foreground">
							lowercase, 2-30 chars, geen spaties. Wordt onderdeel van de URL.
						</p>
					</div>
					<div>
						<Label>Naam (volledig)</Label>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="bv. SureGroup ICT B.V."
						/>
					</div>
					<div className="sm:col-span-2">
						<Label>Intake-mailadres</Label>
						<Input
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onFocus={() => !email && suggestEmail && setEmail(suggestEmail)}
							placeholder={suggestEmail || 'code@in.docr.nl'}
						/>
						<p className="mt-1 text-xs text-muted-foreground">
							Mails naar dit adres worden door Cloudflare Email Routing direct naar de intake-Worker doorgestuurd.
						</p>
					</div>
					<div className="sm:col-span-2">
						<Label>Inkoopfacturen-mail (optioneel)</Label>
						<Input
							value={inkoopEmail}
							onChange={(e) => setInkoopEmail(e.target.value)}
							onFocus={() => !inkoopEmail && suggestInkoop && setInkoopEmail(suggestInkoop)}
							placeholder={suggestInkoop || 'inkoop.code@in.docr.nl'}
						/>
						<p className="mt-1 text-xs text-muted-foreground">
							Apart adres voor inkoopfacturen — mails hierheen krijgen automatisch <code>target=exact</code> en gaan
							door de inkoop-pipeline naar Exact Online. Leeg laten als deze entity geen aparte inkoop-mail heeft.
						</p>
					</div>
					<div className="sm:col-span-2">
						<Label>Verkoopfacturen-mail (optioneel)</Label>
						<Input
							value={verkoopEmail}
							onChange={(e) => setVerkoopEmail(e.target.value)}
							onFocus={() => !verkoopEmail && suggestVerkoop && setVerkoopEmail(suggestVerkoop)}
							placeholder={suggestVerkoop || 'verkoop.code@in.docr.nl'}
						/>
						<p className="mt-1 text-xs text-muted-foreground">
							Apart adres voor verkoopfacturen — mails hierheen krijgen automatisch <code>direction=outgoing</code> en
							lopen door de multi-PDF splitser + verkoop-pipeline naar Exact Online. Leeg laten als deze entity geen
							aparte verkoop-mail heeft.
						</p>
					</div>
				</div>
				{error && (
					<div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
						{error}
					</div>
				)}
				<div className="flex justify-end gap-2 pt-1">
					<Button variant="outline" size="sm" onClick={() => setOpen(false)}>
						Annuleren
					</Button>
					<Button size="sm" disabled={mutation.isPending || !code || !name || !email} onClick={() => mutation.mutate()}>
						{mutation.isPending ? 'Aanmaken…' : 'Aanmaken'}
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}

function EntityRow({ entity, onChanged }: { entity: AdminEntity; onChanged: () => void }) {
	const [editing, setEditing] = useState(false);
	const [name, setName] = useState(entity.name);
	const [email, setEmail] = useState(entity.intake_email);
	const [inkoopEmail, setInkoopEmail] = useState(entity.inkoop_email ?? '');
	const [verkoopEmail, setVerkoopEmail] = useState(entity.verkoop_email ?? '');
	const [error, setError] = useState<string | null>(null);

	const patchMutation = useMutation({
		mutationFn: () => {
			const patch: {
				name?: string;
				intake_email?: string;
				inkoop_email?: string | null;
				verkoop_email?: string | null;
			} = {};
			if (name.trim() !== entity.name) patch.name = name.trim();
			if (email.trim().toLowerCase() !== entity.intake_email) patch.intake_email = email.trim().toLowerCase();
			const inkoopTrim = inkoopEmail.trim().toLowerCase();
			const currentInkoop = entity.inkoop_email ?? '';
			if (inkoopTrim !== currentInkoop) {
				// Empty input → expliciet null sturen (= wis-intent op de API).
				patch.inkoop_email = inkoopTrim === '' ? null : inkoopTrim;
			}
			const verkoopTrim = verkoopEmail.trim().toLowerCase();
			const currentVerkoop = entity.verkoop_email ?? '';
			if (verkoopTrim !== currentVerkoop) {
				patch.verkoop_email = verkoopTrim === '' ? null : verkoopTrim;
			}
			return patchEntity(entity.id, patch);
		},
		onSuccess: () => {
			setError(null);
			setEditing(false);
			onChanged();
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	const deleteMutation = useMutation({
		mutationFn: () => deleteEntity(entity.id),
		onSuccess: () => onChanged(),
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	const confirmDelete = () => {
		const ok = window.confirm(
			`Verwijder entiteit "${entity.name}" (code: ${entity.code})?\n\nAlleen mogelijk als er geen documenten in deze entity staan. De Email Routing-rule wordt automatisch verwijderd.`,
		);
		if (ok) deleteMutation.mutate();
	};

	if (!editing) {
		return (
			<div className="flex items-center justify-between rounded-md border bg-background p-3">
				<div className="min-w-0 space-y-0.5">
					<div className="flex items-center gap-2">
						<Badge variant="muted">{entity.code}</Badge>
						<span className="font-medium">{entity.name}</span>
					</div>
					<p className="font-mono text-xs text-muted-foreground">{entity.intake_email}</p>
					{entity.inkoop_email && (
						<p className="font-mono text-xs text-muted-foreground">
							<span className="text-foreground/70">inkoop:</span> {entity.inkoop_email}
						</p>
					)}
					{entity.verkoop_email && (
						<p className="font-mono text-xs text-muted-foreground">
							<span className="text-foreground/70">verkoop:</span> {entity.verkoop_email}
						</p>
					)}
					{error && <p className="text-xs text-destructive">{error}</p>}
				</div>
				<div className="flex gap-1.5">
					<Button size="sm" variant="outline" asChild>
						<Link to="/admin/entities/$code/exact" params={{ code: entity.code }}>
							<Plug className="mr-1.5 h-4 w-4" />
							Exact
						</Link>
					</Button>
					<Button size="sm" variant="outline" onClick={() => setEditing(true)}>
						Bewerk
					</Button>
					<Button
						size="sm"
						variant="outline"
						className="text-destructive hover:bg-destructive/10 hover:text-destructive"
						onClick={confirmDelete}
						disabled={deleteMutation.isPending}
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-3 rounded-md border bg-background p-3">
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<div>
					<Label>Naam</Label>
					<Input value={name} onChange={(e) => setName(e.target.value)} />
				</div>
				<div>
					<Label>Intake-mail</Label>
					<Input value={email} onChange={(e) => setEmail(e.target.value)} />
				</div>
				<div className="sm:col-span-2">
					<Label>Inkoopfacturen-mail (optioneel)</Label>
					<Input
						value={inkoopEmail}
						onChange={(e) => setInkoopEmail(e.target.value)}
						placeholder="leeg laten = geen aparte inkoop-mail"
					/>
					<p className="mt-1 text-xs text-muted-foreground">
						Apart adres voor inkoopfacturen (target=exact). Leeg maken verwijdert de Cloudflare Email Routing-rule.
					</p>
				</div>
				<div className="sm:col-span-2">
					<Label>Verkoopfacturen-mail (optioneel)</Label>
					<Input
						value={verkoopEmail}
						onChange={(e) => setVerkoopEmail(e.target.value)}
						placeholder="leeg laten = geen aparte verkoop-mail"
					/>
					<p className="mt-1 text-xs text-muted-foreground">
						Apart adres voor verkoopfacturen (direction=outgoing, multi-PDF splitser actief). Leeg maken verwijdert de
						Cloudflare Email Routing-rule.
					</p>
				</div>
			</div>
			<p className="text-xs text-muted-foreground">
				Code "<span className="font-mono">{entity.code}</span>" is niet wijzigbaar.
			</p>
			{error && <p className="text-xs text-destructive">{error}</p>}
			<div className="flex justify-end gap-2">
				<Button
					size="sm"
					variant="outline"
					onClick={() => {
						setEditing(false);
						setName(entity.name);
						setEmail(entity.intake_email);
						setInkoopEmail(entity.inkoop_email ?? '');
						setVerkoopEmail(entity.verkoop_email ?? '');
						setError(null);
					}}
				>
					Annuleren
				</Button>
				<Button size="sm" disabled={patchMutation.isPending} onClick={() => patchMutation.mutate()}>
					<Save className="mr-1.5 h-4 w-4" />
					Opslaan
				</Button>
			</div>
		</div>
	);
}
